import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient, orgUiPath } from '@/core/api';
import {
  autoBootstrapWorkspace,
  isUnregisteredError,
  pickSingleAccessibleOrg,
} from '@/core/workspaceBootstrap';
import { mintOrgSession } from '@/core/sessionAuth';
import {
  isLegacyAppPath,
  isOrganizationsUiPath,
  migrateLegacyPath,
  readReturnQuery,
  resolveAuthenticatedDestination,
} from '@/core/urlRouting';
import { Spinner } from '@/components/primitives/Spinner';
import type { MeResponse, OrganizationSummary } from '@/types/api';

export default function OrgChooserPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingReturn = readReturnQuery(searchParams.toString());
  const [orgs, setOrgs] = useState<OrganizationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectingId, setSelectingId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const enterOrg = useCallback(
    async (orgId: number) => {
      setSelectingId(orgId);
      setError('');
      try {
        await mintOrgSession(orgId);
        let destination = orgUiPath(orgId, '/');
        if (pendingReturn) {
          const me = await apiClient<MeResponse>('/users/me');
          const pathname = pendingReturn.split('?')[0]?.split('#')[0] ?? '/';
          if (isLegacyAppPath(pathname) || isOrganizationsUiPath(pathname)) {
            destination = migrateLegacyPath(pendingReturn, orgId);
          } else {
            destination = resolveAuthenticatedDestination(me, pendingReturn);
          }
        }
        navigate(destination, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not start workspace session');
        setSelectingId(null);
      }
    },
    [navigate, pendingReturn],
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const me = await apiClient<MeResponse>('/users/me');
        if (cancelled) return;
        const soleOrgId = pickSingleAccessibleOrg(me);
        if (soleOrgId != null) {
          void enterOrg(soleOrgId);
          return;
        }
        if (me.organizations.length > 0) {
          setOrgs(me.organizations);
          setLoading(false);
          return;
        }
        const orgId = await autoBootstrapWorkspace();
        if (cancelled) return;
        navigate(orgUiPath(orgId, '/'), { replace: true });
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load workspaces';
        if (isUnregisteredError(message)) {
          try {
            const orgId = await autoBootstrapWorkspace();
            if (cancelled) return;
            navigate(orgUiPath(orgId, '/'), { replace: true });
          } catch (bootstrapErr: unknown) {
            setError(
              bootstrapErr instanceof Error ? bootstrapErr.message : 'Workspace setup failed',
            );
            setLoading(false);
          }
          return;
        }
        setError(message);
        setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [enterOrg, navigate]);

  if (loading || selectingId != null) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="aspectly:min-h-screen aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface aspectly:px-6">
      <div className="aspectly:w-full aspectly:max-w-lg aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:p-8 aspectly:ambient-shadow">
        <h1 className="aspectly:font-headline aspectly:text-2xl aspectly:font-extrabold aspectly:text-on-surface aspectly:mb-2">
          Choose a workspace
        </h1>
        <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:mb-6">
          Select the organization you want to work in.
        </p>

        {error && (
          <div className="aspectly:mb-4 aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {error}
          </div>
        )}

        <ul className="aspectly:space-y-2">
          {orgs.map((org) => {
            const id = org.id;
            if (id == null) return null;
            return (
              <li key={id}>
                <button
                  type="button"
                  disabled={selectingId != null}
                  onClick={() => void enterOrg(id)}
                  className="aspectly:w-full aspectly:text-left aspectly:px-4 aspectly:py-3 aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/20 aspectly:bg-surface-container-low aspectly:hover:bg-surface-container aspectly:transition-colors aspectly:cursor-pointer aspectly:disabled:opacity-60"
                >
                  <span className="aspectly:block aspectly:font-bold aspectly:text-on-surface">{org.name}</span>
                  <span className="aspectly:block aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-0.5">
                    Organization #{id}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
